import {
  FormStateReturn,
  makeUseFormField,
  Path,
  useFormState,
} from '@cotera/client/app/components/forms/hooks/use-form';
import { CodeBlock, Icon, Text } from '@cotera/client/app/components/ui';
import { ChildrenProps, classNames } from '@cotera/client/app/components/utils';
import { Input } from '@cotera/client/v0/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { Assert } from '@cotera/utilities';
import { useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { Button } from '@cotera/client/v0/components/ui/button';
import { DG } from '@cotera/sdk/core';

export const NA_VALUE = 'N/A';

// Re-export JSONNode and helper types for backward compatibility
export type JSONNode = DG.JSONNode;
type Enum = DG.JSONNodeEnum;
type ObjectNode = DG.JSONNodeObject;

type FormState = FormStateReturn<JSONNode>;

const createPath = (
  path: Path,
  formState: Pick<FormState, 'getField'>
): Path => {
  if (path.length === 0) {
    return path;
  }

  const parentPath = createPath(path.slice(0, -1), formState);
  const parent = formState.getField(parentPath);

  if (parent === undefined) {
    return path;
  }

  if (parent.t === 'object') {
    return [...parentPath, 'fields', path.at(-1)!].filter(
      (x) => x !== undefined
    );
  }
  if (parent.t === 'array') {
    return [...parentPath, 'items', path.at(-1)!].filter(
      (x) => x !== undefined
    );
  }

  return [...parentPath, path.at(-1)!];
};

const useFormField = makeUseFormField<JSONNode>((path, formState) => {
  return createPath(path, formState);
});

export const JsonTypeBuilder: React.FC<{
  value: JSONNode;
  onChange: (value: JSONNode) => void;
  bordered?: boolean;
}> = ({ onChange, value, bordered = true }) => {
  // `value` comes from a persisted TipTap attr and is often LLM authored, so it
  // is not guaranteed to be a well formed node. The renderers below walk the
  // tree structurally and would crash on a malformed one.
  const initialType: JSONNode = {
    t: 'object',
    fields: {
      response: {
        ...DG.normalizeJsonNode(value),
        name: 'response',
        nullable: false,
      },
    },
  };

  const formState = useFormState<JSONNode>(initialType);

  useEffect(() => {
    const unsubscribe = formState.subscribe(() => {
      const root = formState.getField(['fields', 'response'])!;
      onChange(root);
    });

    return () => {
      unsubscribe();
    };
  }, [formState, onChange]);

  return (
    <div className="flex flex-col gap-2">
      {/* A backstop, not the narrow layout: rows reflow to two lines long
          before this matters (see `ItemRenderer`). What is left is the case
          where even two lines do not fit — a deeply nested row in a panel
          dragged to a couple of hundred pixels — and there the tree scrolls
          rather than painting its controls over each other. The preview stays
          outside it, since a code block brings its own scrolling. */}
      <div className="overflow-x-auto">
        <Renderer node={initialType} formState={formState} path={[]} />
      </div>
      <div className="flex flex-col">
        <Text.Caption>Shape Preview</Text.Caption>
        <Preview formState={formState} bordered={bordered} />
      </div>
    </div>
  );
};

type RendererProps = {
  node: JSONNode;
  formState: FormState;
  path: Path;
};

const makeNewNode = (t: JSONNode['t']): JSONNode => {
  switch (t) {
    case 'object':
      return {
        t: 'object',
        fields: {
          [v4()]: {
            t: 'string',
            name: 'attr_1',
            nullable: false,
          },
        },
      };
    case 'array':
      return {
        t: 'array',
        items: {
          item: {
            t: 'object',
            fields: {
              [v4()]: {
                t: 'string',
                name: 'attr_1',
                nullable: false,
              },
            },
          },
        },
      };
    case 'enum':
      return {
        t: 'enum',
        values: [NA_VALUE],
        nullable: false,
      };
    case 'timestamp':
      return {
        t: 'timestamp',
        nullable: false,
      };
    default:
      return {
        t,
        nullable: false,
      };
  }
};

/**
 * One level of the tree. It is also the container its rows are measured
 * against (`@container/shape-row`): a row reflows on the width it actually
 * has, which is the panel's width less every indent between it and the panel,
 * not on the width of the window. A row four levels down in a side panel is
 * the case that runs out of room first, and it is the one a viewport query
 * would never see.
 */
const Group: React.FC<ChildrenProps & { depth: number }> = ({
  children,
  depth,
}) => {
  return (
    <div
      className={classNames(
        '@container/shape-row flex flex-col mb-2 w-full border-divider',
        depth > 0 ? 'border-l-1 pl-2' : ''
      )}
    >
      {children}
    </div>
  );
};

const Renderer: React.FC<RendererProps> = (props) => {
  switch (props.node.t) {
    case 'array':
      return <ArrayRenderer {...props} />;
    case 'object':
      return <ObjectRenderer {...props} />;
    case 'enum':
      return <EnumRenderer {...props} />;
    default:
      return <ItemRenderer {...props} />;
  }
};

const EnumRenderer: React.FC<RendererProps> = (props) => {
  const [value, setValue] = useFormField(props.formState, props.path);
  const item = value as Enum;

  return (
    <Group depth={props.path.length}>
      {item.values.map((v, i) => {
        return (
          // The gap between one value and the next belongs to the row, not to
          // the box inside it. As a `mb-2` on the input, it counted as part of
          // that input's margin box, and `items-center` then centred the
          // delete button against box-plus-margin — leaving the trash can
          // sitting visibly above the middle of the field it deletes.
          <div className="w-full flex items-center gap-2 mb-2" key={i}>
            <Input
              // Caps at the old fixed 200px but gives way when the row is
              // narrow — every level of nesting takes width off this row, and
              // a fixed width simply overflowed instead. It has to stay
              // shrinkable: the `N/A` row carries a caption beside it, and
              // `:root` sets `overflow-wrap: break-word`, so a box that
              // refused to yield left the caption narrower than its own
              // longest word and it came apart down the middle of "represent".
              // `text-sm` for the same reason as the name box above.
              className="w-full max-w-[200px] text-sm"
              value={v}
              onChange={(e) => {
                setValue((prev) => {
                  Assert.assert(
                    prev.t === 'enum',
                    `Expected enum, got ${prev.t}`
                  );
                  return {
                    ...prev,
                    values: prev.values.map((x, j) =>
                      i === j ? e.target.value : x
                    ),
                  };
                });
              }}
            />
            <Button
              variant="ghost"
              size={'icon'}
              onClick={() => {
                setValue((prev) => {
                  Assert.assert(
                    prev.t === 'enum',
                    `Expected enum, got ${prev.t}`
                  );
                  return {
                    ...prev,
                    values: prev.values.filter((_, j) => j !== i),
                  };
                });
              }}
            >
              <Icon icon="trash" className="text-destructive" />
            </Button>
            {v === NA_VALUE && (
              <Text.Caption className="min-w-0 text-muted-text">
                {NA_VALUE} is used to represent a missing value.
              </Text.Caption>
            )}
          </div>
        );
      })}
      <Button
        className="w-fit"
        variant="outline"
        onClick={() => {
          setValue((prev) => {
            Assert.assert(prev.t === 'enum', `Expected enum, got ${prev.t}`);
            return {
              ...prev,
              values: [...prev.values, ''],
            };
          });
        }}
      >
        <Icon icon="add" />
        Add Value
      </Button>
    </Group>
  );
};

const ObjectItemRenderer: React.FC<RendererProps & { duplicate?: boolean }> = (
  props
) => {
  const [value, setValue] = useFormField(props.formState, props.path);
  const item = value as JSONNode & { name: string };

  const disabled = props.path.length === 1;

  return (
    <Input
      type="text"
      // `Input` carries `text-base md:text-sm`, the shadcn guard against iOS
      // zooming a form on focus. `md:` is a viewport query at 768px, so under
      // that width the name box alone jumps from 11.375px to 13px while the
      // type select and the Optional label beside it — both plain `text-sm` —
      // stay put, and the row reads as though the value had been enlarged.
      // The guard does nothing for us either way: it wants 16px, and this app
      // sets `html { font-size: 0.8125rem }`, so `text-base` here resolves to
      // 13px and zooms anyway. Pin the size the rest of the row is written in.
      className="text-sm"
      disabled={disabled}
      // `Input` styles `aria-invalid` itself, so the collision shows up on the
      // field that causes it rather than only in the caption under the list.
      aria-invalid={props.duplicate === true}
      value={String(item.name)}
      onChange={(v) => {
        if (!disabled) {
          setValue((prev) => {
            return {
              ...prev,
              name: v.target.value,
            };
          });
        }
      }}
    />
  );
};

const ObjectRenderer: React.FC<RendererProps> = (props) => {
  const [value, setValue] = useFormField(props.formState, props.path);
  Assert.assert(value?.t === 'object', `Expected object, got ${value?.t}`);

  // Insertion order, not alphabetical. Sorting here meant the list resorted on
  // every keystroke, so renaming a field slid the row you were typing in to a
  // new position mid-word; and because a new field starts with a blank name, it
  // landed at the top of the list rather than by the `Add Field` button that
  // had just been clicked. Nothing downstream reads this order — the preview
  // and `responseShapeToShorthand` both sort by name themselves.
  const entries = Object.entries(value.fields);

  // The response is keyed by `name`, not by the opaque id these fields are
  // stored under, so two fields sharing a name collapse into one and the other
  // is silently dropped. Blank names are excluded: every new field starts
  // blank, and flagging two of them before anything has been typed is nagging
  // rather than warning.
  const names = entries.map(([, field]) => field.name ?? '');
  const duplicateNames = new Set(
    names.filter((name, i) => name !== '' && names.indexOf(name) !== i)
  );

  return (
    <Group depth={props.path.length}>
      {entries.map(([key, field]) => {
        return (
          <ItemRenderer
            key={key}
            formState={props.formState}
            path={[...props.path, key]}
            duplicateName={duplicateNames.has(field.name ?? '')}
          />
        );
      })}
      {duplicateNames.size > 0 && (
        <Text.Caption className="mb-2 text-destructive">
          {`Used more than once: ${[...duplicateNames].join(
            ', '
          )}. Only one field of each name reaches the response.`}
        </Text.Caption>
      )}
      {props.path.length > 0 && (
        <Button
          variant="outline"
          className="w-fit"
          onClick={() => {
            setValue((prev) => {
              Assert.assert(
                prev.t === 'object',
                `Expected object, got ${prev.t}`
              );
              return {
                ...prev,
                fields: {
                  ...prev.fields,
                  // `v4()`, as everywhere else a field id is minted here.
                  // `Math.random().toString()` can repeat, and a repeat
                  // overwrites the field already under that key.
                  [v4()]: {
                    t: 'string',
                    name: '',
                    nullable: false,
                  },
                },
              };
            });
          }}
        >
          <Icon icon="add" />
          Add Field
        </Button>
      )}
    </Group>
  );
};

const ArrayRenderer: React.FC<RendererProps> = (props) => {
  return (
    <Group depth={props.path.length}>
      <ItemRenderer
        formState={props.formState}
        path={[...props.path, 'item']}
      />
    </Group>
  );
};

const ItemRenderer: React.FC<
  Omit<RendererProps, 'node'> & { duplicateName?: boolean }
> = (props) => {
  const [parentValue, setParentValue] = useFormField(
    props.formState,
    props.path.slice(0, -1)
  );
  const [value, setValue] = useFormField(props.formState, props.path);

  Assert.assert(
    value !== undefined,
    `no value at path ${props.path.join('.')}`
  );

  const makeOptions = () => {
    if (props.path.length === 1) {
      return ['string', 'boolean', 'object', 'enum'];
    }

    return [
      'string',
      'int',
      'float',
      'boolean',
      'timestamp',
      'object',
      'array',
      'enum',
    ];
  };

  const isArrayItem = parentValue?.t === 'array';
  const canDelete = !isArrayItem && props.path.length > 1;

  // Object, array and enum nodes are the ones `Renderer` descends into below;
  // everything else is a leaf with nothing to fold away.
  const hasChildren =
    value.t === 'object' || value.t === 'array' || value.t === 'enum';
  // Starts expanded, matching the tree's behaviour before collapsing existed —
  // opening the builder on an existing shape should show the whole thing, not
  // require unfolding it first.
  const [collapsed, setCollapsed] = useState(false);

  // What a collapsed row's children amount to, shown beside its name so
  // folding it away doesn't also throw away what was in it. Arrays are left
  // out: an array always has exactly one item shape, so "1 item" would be
  // true of every array row and say nothing about this one.
  const childSummary = (): string | null => {
    if (!collapsed) {
      return null;
    }
    if (value.t === 'object') {
      const n = Object.keys(value.fields).length;
      return `${n} field${n === 1 ? '' : 's'}`;
    }
    if (value.t === 'enum') {
      const n = value.values.length;
      return `${n} value${n === 1 ? '' : 's'}`;
    }
    return null;
  };
  const summary = childSummary();

  return (
    <div className="flex flex-col items-center w-full">
      {/* A grid rather than a flex row, so the disclosure chevron, type,
          Optional and the delete button land at the same x on every row.
          Laid out by flex, each control was placed by whatever happened to
          sit left of it: a row with no name input — an array's element
          shape — pushed its type select to the far left of the row, and a
          row with no delete button (the root) pushed the three columns
          before it a button's width to the right. Nesting indents from the
          left only, so fixed right-hand columns line up at every depth once
          nothing to their left can go missing.

          Name and type both take a share of whatever is left over, rather
          than the type taking a fixed 180px off the top. The builder is drawn
          inside a prompt document, a few hundred pixels wide and narrower
          again at every level of nesting; a fixed type column ate that width
          first and left the name column to collapse, so the field names — the
          one thing on the row that is not recoverable from looking at it —
          were the first thing to disappear. Sharing the space means both
          narrow together, and a type name ("timestamp" is the longest) has
          far less to lose to truncation than a name does. The two fixed
          columns are the two icon buttons, at their own size.

          Both share columns have a floor, and under it the row stops
          competing for one line and takes two: the name keeps the first to
          itself, and the type and Optional drop to the second. Sharing alone
          still let a side panel dragged narrow take the name box down to an
          empty slot and the type select down to its chevron, and past that
          the controls drew over each other, since a box whose track has run
          out does not stop painting. Two lines is what a row this narrow has
          room for; the alternative is a row of blank boxes, or a sideways
          scroll to reach a checkbox.

          The columns are placed rather than flowed, since the two layouts
          disagree about which cell goes where, and the breakpoint is on the
          enclosing `Group` (see there) so the query is about this row's own
          width rather than the window's. */}
      <div className="grid w-full grid-cols-[1.25rem_minmax(6rem,1fr)_auto_2.25rem] @sm/shape-row:grid-cols-[1.25rem_minmax(5rem,1fr)_minmax(6rem,1fr)_auto_2.25rem] items-center gap-x-2 gap-y-1 mb-2">
        {hasChildren ? (
          <Button
            variant="ghost"
            size={'icon'}
            className="col-start-1 row-start-1 h-5 w-5"
            aria-label={collapsed ? 'Expand' : 'Collapse'}
            onClick={() => setCollapsed((prev) => !prev)}
          >
            <Icon
              icon="chevron-right"
              size="small"
              className={classNames(
                'transition-transform text-muted-text',
                !collapsed && 'rotate-90'
              )}
            />
          </Button>
        ) : (
          <span className="col-start-1 row-start-1" />
        )}
        {/* Two lines wide, one line narrow: the name is the row's own line
            when there is not room for the type beside it. */}
        <div className="col-start-2 col-end-4 row-start-1 flex min-w-0 items-center gap-2 @sm/shape-row:col-end-3">
          {'name' in value ? (
            <ObjectItemRenderer
              formState={props.formState}
              path={props.path}
              node={value}
              duplicate={props.duplicateName}
            />
          ) : isArrayItem ? (
            // The array's element shape, which has no name of its own. Left as
            // a bare type dropdown it read as a field whose name had failed to
            // load rather than as "this is what one of these contains".
            <Text.Caption className="truncate pl-3">each item</Text.Caption>
          ) : (
            <span />
          )}
          {summary !== null && (
            <Text.Caption className="shrink-0 whitespace-nowrap">
              {summary}
            </Text.Caption>
          )}
        </div>
        <Select
          value={value.t}
          onValueChange={(v) => {
            setValue((prev) => {
              // Carry the field name across, but not `fields`/`items`/`values`
              // from the type being replaced — a node holding another type's
              // keys is exactly what makes a path resolve to a non-node.
              const next = makeNewNode(v as JSONNode['t']);
              return prev.name === undefined
                ? next
                : { ...next, name: prev.name };
            });
          }}
        >
          {/* `min-w-0`: a grid item's automatic minimum is its content, and
              the trigger's content does not wrap, so without this the select
              refuses to shrink and spills over the columns to its right. */}
          <SelectTrigger className="col-start-2 row-start-2 w-full min-w-0 @sm/shape-row:col-start-3 @sm/shape-row:row-start-1">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {makeOptions().map((x) => (
              <SelectItem key={x} value={x}>
                {x}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="col-start-3 row-start-2 flex items-center space-x-2 @sm/shape-row:col-start-4 @sm/shape-row:row-start-1">
          <Checkbox
            id={`nullable-${props.path.join('-')}`}
            checked={value.nullable || false}
            onCheckedChange={(checked) => {
              setValue((prev) => ({
                ...prev,
                nullable: checked as boolean,
              }));
            }}
          />
          {/* "Optional" is the word the people writing these prompts use, so
              it stays, even though what the checkbox writes is `nullable` and
              what the preview prints is `| null`. The `title` is what keeps
              the two from reading as a contradiction: the field is always
              there, it just comes back empty. Renaming this to "Nullable"
              would make the label agree with the preview at the cost of
              saying nothing to the person filling the form in. */}
          <label
            htmlFor={`nullable-${props.path.join('-')}`}
            title="Optional fields are always present in the response and come back as null when the model has no value for them."
            className="text-sm text-muted-text whitespace-nowrap"
          >
            Optional
          </label>
        </div>
        {canDelete ? (
          <Button
            variant="ghost"
            size={'icon'}
            className="col-start-4 row-start-1 @sm/shape-row:col-start-5"
            onClick={() => {
              setParentValue((prev) => {
                Assert.assert(
                  prev.t === 'object',
                  `Expected object, got ${prev.t}`
                );
                const newFields = { ...prev.fields };
                delete newFields[props.path.at(-1)!];
                return {
                  ...prev,
                  fields: newFields,
                };
              });
            }}
          >
            <Icon icon="trash" className="text-destructive" />
          </Button>
        ) : (
          // Holds the column open, so a row that cannot be deleted does not
          // drag the three columns left of it out of line with its siblings.
          <span className="col-start-4 row-start-1 @sm/shape-row:col-start-5" />
        )}
      </div>
      {hasChildren && !collapsed && (
        // Indented past the disclosure column, so a group's rail and the rows
        // inside it begin under this row's *name* rather than under its
        // chevron. Undented, a row's children drew to the left of the name
        // they belong to — an enum's values lined up with their own field's
        // siblings, reading as more fields of the parent object instead of as
        // the values of the field directly above them.
        <div className="w-full pl-7">
          <Renderer
            formState={props.formState}
            path={props.path}
            node={value}
          />
        </div>
      )}
    </div>
  );
};

const Preview: React.FC<{
  bordered?: boolean;
  formState: FormState;
}> = ({ formState, bordered = true }) => {
  // Force re-render on any form state change by subscribing directly
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const unsubscribe = formState.subscribe(() => {
      // Trigger re-render on any change
      forceUpdate({});
    });
    return unsubscribe;
  }, [formState]);

  const root = formState.getField([])!;
  const previewNode =
    (root as ObjectNode)?.['fields']?.['response'] ?? undefined;

  if (!previewNode) {
    return null;
  }

  return (
    <CodeBlock
      language="json"
      code={prettyPrintNode(previewNode)}
      bordered={bordered}
      showCopyButton={true}
    />
  );
};

// Pretty printer that works directly with JSONNode objects
export const prettyPrintNode = (
  node: JSONNode,
  indentLevel: number = 0
): string => {
  const indent = '  '.repeat(indentLevel);
  const nextIndent = '  '.repeat(indentLevel + 1);

  switch (node.t) {
    case 'object': {
      const fieldEntries = Object.entries(node.fields).sort(([, a], [, b]) =>
        (a.name ?? '').localeCompare(b.name ?? '')
      );
      if (fieldEntries.length === 0) {
        return `${indent}{\n${indent}}`;
      }

      const formattedFields = fieldEntries.map(([_, field]) => {
        const fieldType = prettyPrintNode(field, indentLevel + 1);
        const nullableSuffix = field.nullable ? ' | null' : '';
        // Remove all leading indentation that was added by the recursive call
        const cleanFieldType = fieldType.replace(/^\s+/, '');
        return `${nextIndent}"${field.name}": ${cleanFieldType}${nullableSuffix}`;
      });

      return `${indent}{\n${formattedFields.join(',\n')}\n${indent}}`;
    }

    case 'array': {
      const itemType = prettyPrintNode(node.items.item, indentLevel + 1);
      // Add proper indentation to the item
      const indentedItem = itemType
        .split('\n')
        .map((line) => {
          return line.startsWith('  ') ? line : `${nextIndent}${line}`;
        })
        .join('\n');
      return `${indent}[\n${indentedItem}\n${indent}]`;
    }

    case 'enum': {
      return `"${node.values.join(' | ')}"`;
    }

    case 'string':
    case 'int':
    case 'float':
    case 'boolean':
    case 'timestamp': {
      return `"${node.t}"`;
    }
    default: {
      return `"unknown"`;
    }
  }
};
